import { useCallback } from 'react';
import { clearJavaScriptCache, forceHardReload } from './utils';

/**
 * Custom hook for handling session expiry with cache clearing
 * This helps prevent ChunkLoadError issues when sessions expire
 */
export const useSessionExpiry = () => {
  /**
   * Handles session expiry with proper cache clearing
   * This should be called when a session expires to prevent ChunkLoadError
   */
  const handleSessionExpiry = useCallback(async (): Promise<void> => {
    try {
      // Clear JavaScript cache
      await clearJavaScriptCache();

      // Force a hard reload to get fresh bundles
      forceHardReload();
    } catch (error) {
      console.warn('Failed to handle session expiry properly:', error);
      // Fallback to simple reload
      window.location.href = '/sign-in';
    }
  }, []);

  /**
   * Clears JavaScript cache without reloading
   * Useful for proactive cache management
   */
  const clearCache = useCallback(async (): Promise<void> => {
    await clearJavaScriptCache();
  }, []);

  /**
   * Forces a hard reload of the current page
   * This ensures fresh JavaScript bundles are loaded
   */
  const hardReload = useCallback((): void => {
    forceHardReload();
  }, []);

  return {
    handleSessionExpiry,
    clearCache,
    hardReload,
  };
};
